<template>
  <div>
    <AdminActions
      v-model="item"
      :title="title"
      :readOnly="readOnly"
      @edit-click="toggleReadOnly"
      @cancel-click="onCancel()"
      @save-click="onSubmit()"
    >
    </AdminActions>
    <div v-if="item">
      <div class="setting-section-block">
        <div class="section-top">
           <StatusCard
                v-if="item.status"
                :status="item.status"
                :id="item.id"
                :hideCheckStatus="hideCheckStatus"
                :lastLoginTime="formatLastLoginTime"
            >
            <template v-slot:header>
              <MerhcantHistoryStatusHeader :item="item"></MerhcantHistoryStatusHeader>
            </template>
            </StatusCard>
          <AGCMerchantFileUploader v-model="item.banner"
                :rules="{ required: !item.banner, ...bannerRules }"
                :fieldName="'banner'"
                name="banner"
                upload-type="merchantBanner"
                public-api
          >
            <template v-slot="{ loading, fileChooserHandler }">
              <div class="cover-image">
                <img v-if="item.banner"
                    :src="item.banner | imagePath(uploadType, 'default', true, t)"
                >
                <img v-else
                    src="../../assets/img/merchant/empty-cover-photo.png"
                >
              </div>
              <div class="button-wrapper">
                <b-button v-if="!readOnly"
                          :disabled="loading"
                          @click="fileChooserHandler">
                  <b-icon-camera-fill class="desktop-icon"
                                      font-scale="0.9"
                                      variant="secondary">
                  </b-icon-camera-fill>
                  <b-icon-pencil-fill class="mobile-icon"
                                      font-scale="0.9"
                                      variant="secondary">
                  </b-icon-pencil-fill>
                  {{ loading ? 'Loading...' : '封面圖片' }}
                </b-button>
                  <b-button v-if="!readOnly"
                            @click="handleEdit(item.banner)"
                            :disabled="!item.banner"
                            class="desktop-icon">
                  <b-icon-crop font-scale="0.9"></b-icon-crop>裁剪圖片
                </b-button>
              </div>
            </template>
          </AGCMerchantFileUploader>
          <CropperModal
            v-model="cropperModal"
            :upload-type="uploadType"
            :public-api="publicApi"
            :aspectRatio="aspectRatio"
            @cropped="reloadImages"
          ></CropperModal>
        </div>
        <div class="section-middle">
          <div class="profile-image">
              <AGCMerchantFileUploader v-model="item.logo"
                    :rules="{ required: !item.logo, ...logoRules }"
                    :fieldName="'logo'"
                    name="logo"
                    upload-type="merchantLogo"
                    public-api
              >
                <template v-slot="{ fileChooserHandler }">
                  <img
                      v-if="item.logo"
                      :src="item.logo | imagePath('merchantLogo')"
                      v-on="!readOnly ? { click: fileChooserHandler } : {}"
                  >
                  <div v-if="!readOnly" class="upload-icon" @click="fileChooserHandler">
                    <b-icon-camera-fill></b-icon-camera-fill>
                  </div>
                </template>
              </AGCMerchantFileUploader>
          </div>
        </div>
        <div class="section-bottom">
          <ValidationObserver ref="form" v-slot="{errors, failed }">
            <NotificationMessage :messages="errors" v-if="failed"></NotificationMessage>
            <b-form class="info" @submit.prevent>
              <CompanySetting v-model="item"
                              @is-save="title=item.name"
                              :readOnly="readOnly"
              >
              </CompanySetting>
              <MerchantListSetting  v-model="item"
                                    v-if="role === 'admin'"
                                    :readOnly="readOnly"
              >
              </MerchantListSetting>
              <SaleSetting  v-model="item"
                            v-if="role === 'admin'"
                            :readOnly="readOnly"
              >
              </SaleSetting>
              <PersonalSetting  v-model="item"
                                :role="role"
                                :readOnly="readOnly"
              >
              </PersonalSetting>
              <PasswordSetting  v-model="item"
                                v-if="role === 'merchant'"
                                :readOnly="readOnly"
              >
              </PasswordSetting>
              <BankSetting  v-model="item"
                            v-if="role === 'admin' || role === 'sale' || userClass === 'PREMIUM'"
                            :readOnly="readOnly"
                            ref="bankSetting"
              >
              </BankSetting>
            </b-form>
          </ValidationObserver>
        </div>
      </div>
      <b-alert
        show
        variant="info"
        v-if="role === 'merchant'"
        class="bottom-alert"
      >
        <div>申請成為星級用戶，上載付費優惠券進行預售以率先獲取優惠收益，體驗更全面的優惠功能。</div>
        <div>
          請於「常見問題」了解有關付費優惠券詳情。歡迎電郵至
          <a href="mailto:eCoupon@ulifestyle.com.hk">eCoupon@ulifestyle.com.hk</a> 申請開通 或 查詢更多。
        </div>
        <div class="mt-1" style="font-size: 0.7rem">*受條款或細則約束</div>
      </b-alert>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import {
  tap,
  pluck,
  startWith,
  switchMap,
} from 'rxjs/operators';
import http from '@/helpers/http';
import {
  BForm,
  BIconCameraFill,
  BIconPencilFill,
  BIconCrop,
  BButton,
  BAlert,
} from 'bootstrap-vue';
import getStatus from '@/helpers/status';
import AdminActions from '@/components/merchant/AdminActions.vue';
import CropperModal from '@/components/image/CropperModal.vue';
import CompanySetting from '@/components/merchant/CompanySetting.vue';
import PersonalSetting from '@/components/merchant/PersonalSetting.vue';
import MerchantListSetting from '@/components/merchant/MerchantListSetting.vue';
import PasswordSetting from '@/components/merchant/PasswordSetting.vue';
import BankSetting from '@/components/merchant/BankSetting.vue';
import SaleSetting from '@/components/merchant/SaleSetting.vue';
import AGCMerchantFileUploader from '@/components/form/AGCMerchantFileUploader.vue';
import StatusCard from '@/components/StatusCard.vue';
import MerhcantHistoryStatusHeader from '@/components/merchant/MerhcantHistoryStatusHeader.vue';
import imagePath from '@/filters/image-path';
import formMixin from '@/mixins/form';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash/isEqual';
import { getErrorMsg } from '../../helpers/error-msg';
import toastSettings from '../../helpers/toast-settings';
import NotificationMessage from '../../components/NotificationMessage.vue';

export default {
  mixins: [formMixin],
  props: {
    publicApi: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    AdminActions,
    CropperModal,
    CompanySetting,
    PersonalSetting,
    MerchantListSetting,
    PasswordSetting,
    BankSetting,
    SaleSetting,
    AGCMerchantFileUploader,
    BIconCameraFill,
    BIconPencilFill,
    BIconCrop,
    BButton,
    BForm,
    BAlert,
    StatusCard,
    MerhcantHistoryStatusHeader,
    NotificationMessage,
  },
  data: () => ({
    item: null,
    originalItem: null,
    submitItem: null,
    readOnly: true,
    logoRules: {
      image: true,
      ext: ['jpg', 'jpeg', 'png', 'gif'],
      size: 2048,
      ratio: '1:1',
    },
    bannerRules: {
      image: true,
      ext: ['jpg', 'jpeg', 'png', 'gif'],
      size: 2048,
    },
    isEditMerchant: true,
    cropperModal: {
      show: false,
      image: null,
    },
    uploadType: 'merchantBanner',
    t: null,
    title: '',
  }),
  computed: {
    ...mapGetters(['role']),
    ...mapState(['user']),
    backUrl() {
      return (this.role === 'admin' || this.role === 'sale') ? `/${this.role}/merchants` : null;
    },
    id() {
      return (this.role === 'admin' || this.role === 'sale') ? this.$route.params.id : this.user.id;
    },
    status() {
      return getStatus(this.item);
    },
    apiUrl() {
      return (this.role === 'admin' || this.role === 'sale') ? `/agc-${this.role}-api/merchants/` : '/agc-merchant-api/setting/';
    },
    hideCheckStatus() {
      if (this.role === 'merchant') {
        return true;
      }
      return false;
    },
    userClass() {
      return this.user.class;
    },
    formatLastLoginTime() {
      if ((this.role === 'admin' || this.role === 'sale') && this.item.lastLoginTime) {
        return this.item.lastLoginTime.substring(0, 19);
      }
      return '';
    },
    aspectRatio() {
      return 1125 / 480;
    },
  },
  subscriptions() {
    const getData$ = (id) => http({
      method: 'GET',
      url: this.apiUrl + id,
    }, {
      next: (data) => {
        data.oldPassword = '';
        data.newPassword = '';
        data.confirmNewPassword = '';
      },
    });

    return {
      data$: this.$watchAsObservable('id')
        .pipe(
          pluck('newValue'),
          startWith(this.id),
          switchMap(getData$),
          tap((merchant) => {
            this.item = merchant;
            this.item.contactEmail = this.item.contactEmail.split(',');
            this.title = merchant.name;
            this.originalItem = cloneDeep(this.item);
          }),
        ),
    };
  },
  filters: {
    imagePath,
  },
  methods: {
    reloadImages() {
      this.t = new Date().getTime();
    },
    handleEdit(image) {
      this.cropperModal = {
        show: true,
        image,
      };
    },
    async onSubmit() {
      if (!(await this.validate({ isScrollToTopIfFail: true }).toPromise())) {
        return;
      }
      this.loading = true;
      http(
        {
          method: 'POST',
          url: `${this.apiUrl}update`,
          data: {
            merchantSetting: this.filterSubmitItem(),
            merchantId: this.id,
            redemptionId: this.$route.params.id,
          },
        },
        {
          error: ({ data: { msg } }) => {
            this.$bvToast.toast(getErrorMsg(msg), {
              ...toastSettings,
              title: '提交失敗',
              toaster: 'b-toaster-top-center',
            });
          },
          next: () => {
            this.item.oldPassword = '';
            this.item.newPassword = '';
            this.item.confirmNewPassword = '';
            this.originalItem = cloneDeep(this.item);
            this.toggleReadOnly();
            this.$refs.bankSetting.updateBankInfo();
          },
          complete: () => {
            this.loading = false;
          },
        },
      ).toPromise();
    },
    toggleReadOnly() {
      this.readOnly = !this.readOnly;
    },
    onCancel() {
      this.item = cloneDeep(this.originalItem);
      this.toggleReadOnly();
    },
    filterSubmitItem() {
      this.submitItem = Object.fromEntries(
        Object.entries(this.item)
          .filter(([key]) => !isEqual(this.item[key], this.originalItem[key])),
      );
      if (Object.hasOwn(this.submitItem, 'newPassword')) {
        delete this.submitItem.confirmNewPassword;
        this.submitItem.username = this.item.username;
      }
      return this.submitItem;
    },
  },
};
</script>

<style lang="scss" scoped>
.setting-section-block{
  background: #fff;
  box-sizing: border-box;
  border: 1px solid #DDE1E5;
  border-radius: 4px;
  overflow: hidden;
  margin:auto;
  max-width:800px;
  margin-bottom:10px;

  @media (max-width: 765px) {
    padding:0px 16px;
    border:0px;
    font-size: 14px;
  }

  @media (max-width: 576px) {
    border: 0px;
    border-radius: 0px;
    background: none;
    margin-bottom:0px;
  }

  label {
    font-weight: 400;
    font-size: 15px;

    @media (max-width: 576px) {
      font-size: 0.98em;
      padding-right: 5px;
    }
  }

  & .section-top{
    width:100%;
    display:inline-block;
    position: relative;
    background-color: none;
    & .cover-image{
      width:100%;
      min-height:350px;
      & img{
        width:100%;
      }
      @media (max-width: 765px) {
        min-height: auto;
      }
    }
    & .button-wrapper{
      position: absolute;
      bottom:10px;
      right: 2px;
      display: inline-block;
      & button{
        box-sizing:border-box;
        margin: 0 16px 6px 0;
        padding: 8px 12px;
        height: auto;
        font-size: 0.875rem;
        background-color: #EBEEF0;
        color: #636D77;
        font-weight:500;
        & svg{
          margin-right: 4px;
          position: relative;
          top: -2px;
          font-size: 110% !important;
        }
      }
    }
    .btn-secondary{
      background-color: #F9F9FA !important;
      border: 1px solid #DDE1E5;
    }
    &::v-deep .file-uploader__error {
      display: block;
      text-align: center;
    }
  }
  & .section-middle{
    width:100%;
    display:inline-block;
    position: relative;
    min-height: 65px;
    background: #fff;

    & .profile-image{
      display:inline-block;
      position: absolute;
      top:-50px;
      left:25px;
      cursor:pointer;

      @media (max-width: 576px) {
        left: 10px;
      }
      & img{
        border: 3px solid #ffffff;
        width: 100%;
        min-width:100px;
        min-height:100px;
        max-width:100px;
        max-height:100px;
        border-radius: 50%;
        overflow: hidden;
      }
      & .upload-icon{
        position: absolute;
        right: 0;
        bottom: 0;
        box-sizing: border-box;
        border: 1.5px solid #FFFFFF;
        border-radius: 50%;
        width: 1.9375rem;
        height: 1.9375rem;
        background: #EBEEF0;
        text-align: center;
        color: #636D77;
      }
      &::v-deep .file-uploader__error {
        position: absolute;
        width: 160px;
      }
    }
  }
  & .section-bottom{
    width:100%;
    display:inline-block;
    padding: 0 24px;
    background: #fff;
    margin-top: -5px;

    @media (max-width: 765px) {
      padding: 0 12px;
    }
    &::v-deep {
      & .title{
        color: #3A4047;
        font-size: 1.125rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 25px;
      }
      & .sub-title{
        color: #636D77;
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: 0;
        line-height: 20px;
      }
      & .border-bottom{
        border-bottom: 1px solid #EBEEF0 !important;
        margin-left: 0;
        margin-right: 0;
        box-sizing:border-box;
        padding: 10px 15px 6px 0 !important;
        & div:not(.thumbnail-preview-item){
          padding:0px;
          & label{
            color:#636d77;
            font-weight:400;
          }
        }
      }
      .form-group{
        padding:5px 0px;
        margin-bottom:0px;
        box-sizing:border-box;
      }
      .form-control{
        background:#F9F9FA;
        font-size: 15px;
        @media (max-width: 576px) {
          font-size: 0.98em;
        }
      }
      .form-control:disabled{
        background:none;
        border:none;
      }
      .btn {
        line-height: 18px;
        font-weight: 500;
        font-size: 14px;

        &-light {
          background-color: #f9f9fa;
          border: 1px solid #dde1e5;
          color: #636d77;
        }
      }
    }
  }
}

.info {
  .my-4 {
    margin-top: 32px !important;
    margin-bottom: 0px !important;
  }

  div:first-child {
    .my-4 {
      margin-top: 12px !important;
    }
  }
}

#password {
  .row{
    padding: 20px 0 10px 0 !important;
    margin-bottom: 32px;
  }
}

.desktop-icon {
  @media (max-width: 576px) {
    display: none !important;
  }
}

.mobile-icon {
  display: none !important;

  @media (max-width: 576px) {
    display: inline-block !important;
  }
}

.bottom-alert {
  margin: auto;
  max-width: 800px;
  border: 1px solid #e0effb;
  font-size: 0.85rem;

  @media (max-width: 576px) {
    margin: 0px 16px;
  }
}
</style>
